import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchButton, SearchField } from './SearchComponents';
import { selectAllPoints } from '../map/slice/mapSelectors';
import * as searchActions from './searchSlice';
import * as selectedItemsActions from '../selectedItems/selectedItemsSlice';
import { PinPoint } from '../points/PinPoint';
import { ReusableModal } from '../modals/ReusableModal';
import { RootState } from '@/store';
import { selectPinnedPoints } from '../points/pointsSlice';

export type PointSearchHandler = (value: {
  id: string;
  itemType: string;
  selectionKey: string | undefined;
}) => void;

export type ReduxPointSearchProps = {
  value?: string | number;
  placeholder?: string;
  isSearching?: boolean;
  handler?: PointSearchHandler;
  className?: string;
  style?: React.CSSProperties;
};

export const ReduxPointSearch: React.FC<ReduxPointSearchProps> = (props) => {
  const dispatch = useDispatch();
  const [isSearching, setIsSearching] = useState<boolean>(
    props.isSearching ?? false,
  );
  const searchQuery = useSelector(
    (state: RootState) => state.search.searchQuery,
  );
  const endSearch = () => {
    setIsSearching(false);
    dispatch(searchActions.queryChanged({ searchQuery: null }));
  };

  return (
    <>
      <SearchButton
        style={props.style}
        className={props.className}
        value={props.value}
        placeholder={props.placeholder}
        onClick={() => setIsSearching(true)}
      />
      <ReusableModal
        header="Point Search"
        isOpen={isSearching}
        onClose={endSearch}
      >
        <SearchField
          onChange={(value) =>
            dispatch(searchActions.queryChanged({ searchQuery: value }))
          }
          value={searchQuery || ''}
        />
        <ReduxPointSearchResults
          handler={props.handler}
          setIsSearching={setIsSearching}
        />
      </ReusableModal>
    </>
  );
};

type ReduxPointSearchResultsProps = {
  handler?: PointSearchHandler;
  setIsSearching: (isSearching: boolean) => void;
};

const ReduxPointSearchResults: React.FC<ReduxPointSearchResultsProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const searchResults = useSelector(searchActions.selectFilteredPointIds);
  const pinnedPoints = useSelector(selectPinnedPoints);
  // if specific selectionKey and itemType is set in searchSlice,
  // pass that to searchResult to use with dispatch
  const itemType =
    useSelector((state: RootState) => state.search.itemType) || 'point';
  const selectionKey = useSelector(
    (state: RootState) => state.search.selectionKey,
  );
  const [selectedResult, _setSelectedResult] = useState(0);
  const selectedResultRef = useRef(selectedResult);
  const searchResultsRef = useRef(searchResults);

  useEffect(() => {
    // keep searchResultsRef up to date with searchResults for access to keybindings
    searchResultsRef.current = searchResults;
  }, [searchResults]);

  const setSelectedResult = (data: number) => {
    selectedResultRef.current = data;
    _setSelectedResult(data);
  };

  const handleClick: PointSearchHandler = async ({
    id,
    itemType,
    selectionKey,
  }) => {
    // having to pass handleClick and setIsSearching so far down makes me wonder if it would be better practice to "flatten" this search modal structure into one component
    // pass props to the handler so it gets all information necessary and allows more complex handlers to be passed
    // https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
    if (props.handler) {
      props.handler({ id, itemType, selectionKey });
    } else {
      // default behavior is to select the item clicked:
      dispatch(
        selectedItemsActions.itemClicked({ id, itemType, selectionKey }),
      );
    }

    // dismiss modal: could just do this in response to success?
    props.setIsSearching(false);
    dispatch(searchActions.queryChanged({ searchQuery: null }));
  };

  // set up keybindings for selecting search results
  useEffect(() => {
    const searchKeys = (event: KeyboardEvent) => {
      // tab key
      if (event.keyCode === 9) {
        event.preventDefault();
        setSelectedResult(selectedResultRef.current + 1);
      }

      // enter key
      if (event.keyCode === 13) {
        // handle click of active search result
        event.preventDefault();
        const itemId = searchResultsRef.current[selectedResultRef.current];
        handleClick({ id: itemId, itemType, selectionKey });
      }
    };

    window.addEventListener('keydown', searchKeys);

    return () => {
      window.removeEventListener('keydown', searchKeys);
    };
  }, []);
  // console.log(JSON.stringify(searchResults))
  return (
    <>
      <ul className="list-group list-group-flush">
        {searchResults.map((result, index) => (
          <ReduxPointSearchResult
            handleClick={handleClick}
            key={result}
            selectedResult={index === selectedResult}
            id={result}
            itemType={itemType}
            selectionKey={selectionKey}
            handler={props.handler}
            setIsSearching={props.setIsSearching}
          />
        ))}
      </ul>
      <h6 className="mt-3">Pinned Points</h6>
      <ul className="list-group list-group-flush">
        {pinnedPoints
          .slice(0)
          .reverse()
          .map((result: any, index: number) => (
            <ReduxPointSearchResult
              handleClick={handleClick}
              key={result}
              selectedResult={selectedResult === index + searchResults.length}
              id={result}
              itemType={itemType}
              selectionKey={selectionKey}
              handler={props.handler}
              setIsSearching={props.setIsSearching}
            />
          ))}
      </ul>
    </>
  );
};

export type ReduxPointSearchResultProps = {
  id: string;
  itemType: string;
  selectionKey: string | undefined;
  selectedResult: boolean;
  handler?: PointSearchHandler;
  handleClick: PointSearchHandler;
  setIsSearching: (isSearching: boolean) => void;
};

export const ReduxPointSearchResult: React.FC<ReduxPointSearchResultProps> = (
  props,
) => {
  const { id, itemType, selectionKey, selectedResult } = props;
  const points = useSelector(selectAllPoints);

  const itemInfo = useMemo(() => points[id], [points, id]);

  if (!itemInfo) {
    return null;
  }

  return (
    <li
      className={`list-group-item list-group-item-action ${
        selectedResult ? 'active' : ''
      }`}
      onClick={() => props.handleClick({ id, itemType, selectionKey })}
    >
      <small>
        <strong>#{itemInfo.id}</strong>
      </small>
      <h6>
        {itemInfo.name} ({itemInfo.loc_id})
      </h6>
      {itemInfo.capacity_components?.length > 0 && (
        <>
          <div>
            <small>
              <strong>Capacities</strong>
            </small>
          </div>
          {itemInfo.capacity_components.map((cc) => (
            <div key={cc.id}>
              <small>
                <strong>#{cc.id}</strong> {cc.label ?? cc.description}
              </small>
            </div>
          ))}
        </>
      )}
      <div className="clearfix">
        <small className="float-start">
          {itemInfo.tsp_name}: {itemInfo.receipt_zone_name}
        </small>
        <small className="float-end">
          {itemInfo.loc_cnty}
          <PinPoint id={id} />
        </small>
      </div>
      {itemInfo.up_dn_name && (
        <small>
          Connects to:
          {itemInfo.up_dn_name}
        </small>
      )}
      {!itemInfo.node && (
        <div className="float-end text-danger">NOT MAPPED</div>
      )}
    </li>
  );
};
